import { createFileRoute, Link } from "@tanstack/react-router";
import { pageMeta, ldScripts, breadcrumbLd, faqLd } from "@/lib/seo";
import { SiteNav } from "@/components/layout/SiteNav";
import { SiteFooter } from "@/components/layout/SiteFooter";
import { SectionHeading } from "@/components/ui-ext/SectionHeading";
import { EyebrowLabel } from "@/components/ui-ext/Badge";
import { PortfolioFilters } from "@/components/portfolio/PortfolioFilters";
import { PORTFOLIO } from "@/content/portfolio";
import { Picture } from "@/components/media/Picture";
import { siteConfig } from "@/config/site";


const PORTFOLIO_FAQS = [
  {
    q: `How does ${siteConfig.name} decide which engagements go into the portfolio?`,
    a: "We only publish work where we can attach dated, sourced metrics — a Semrush snapshot, a Google Search Console export, or a Google Business Profile insights screenshot pulled at two points in time. If a client's contract ends before we've captured a defensible before/after window, or if the client asks us to keep the relationship private, the project is left out. Being in the portfolio is not a reward for a good outcome; it is a function of whether the evidence trail exists and the client has consented to it being shown.",
  },

  {
    q: "What counts as evidence on an engagement page?",
    a: "Three tiers. First, first-party analytics screenshots — Search Console queries, pages, and impressions/clicks trends, timestamped. Second, third-party tool data — Semrush organic keyword counts, estimated traffic, and referring-domain growth, which is directional but useful for cross-referencing our own numbers. Third, operational artefacts — the actual pages we shipped, schema markup we deployed, or citation batches we built. We prefer showing the underlying dashboard over a designed infographic because a screenshot is harder to fabricate than a chart.",
  },
  {
    q: "Why do some engagements have a full case study and others don't?",
    a: "A case study is a considerably heavier lift than a portfolio entry — it requires baseline documentation from before we started, a written methodology, and client sign-off on publishing tactical detail that competitors could read. Not every client wants that level of transparency, and some engagements are still too early to tell a complete story. The badge on each project card tells you honestly which tier you're looking at; we don't pad the count by relabelling short entries as case studies.",
  },
  {
    q: "Do you ever remove or update portfolio entries?",
    a: "Yes. If a client's site is redesigned, sold, or drops rankings after we've left (which does happen — SEO gains are not permanent if nobody maintains them), we either update the entry with a note or retire it. An agency portfolio frozen in 2021 with no visible dates is a sign nobody's checking whether the claims still hold.",
  },
  {
    q: "Should I trust metrics from a third-party tool like Semrush over an agency's own numbers?",
    a: "Neither in isolation. Semrush and similar tools estimate traffic and keyword counts from crawled data and clickstream panels — useful for third-party verification because the agency doesn't control the inputs, but imprecise at the level of individual pages. First-party Search Console and analytics data is exact for the property but is controlled by the agency showing it to you, which is why we publish both where we can: one as ground truth, one as an independent cross-check.",
  },
  {
    q: "What red flags should I look for when evaluating any SEO agency's portfolio, not just ours?",
    a: "Round, suspiciously tidy percentages with no baseline stated (a claimed \"300% increase\" is meaningless without knowing whether it went from 4 to 16 visits or 4,000 to 16,000). Anonymised clients with no way to verify the business exists. Screenshots with no visible date range or tool name. Metrics that only ever go up, with no mention of algorithm-update dips or seasonal dents, which every real campaign experiences. And testimonials with no link to the business — a genuine agency portfolio should hold up to a five-minute independent check.",
  },
  {
    q: "Can I see the raw data behind a specific engagement before hiring you?",
    a: "For active discussions, yes — we'll walk a prospective client through the live Search Console property or Semrush project for a comparable engagement on a call, which is a level of access most agencies won't offer because it's harder to embellish live data than a static case study PDF. Ask any agency you're evaluating for the same; a confident answer is a good filter.",
  },
];


export const Route = createFileRoute("/portfolio/")({
  head: () => ({ ...pageMeta({
    path: "/portfolio/",
    title: `SEO Portfolio & Client Results — Real Semrush Data | ${siteConfig.name}`,
    description:
      `Selected SEO engagements from ${siteConfig.name} with real Semrush and Google Search Console data, dated windows, and named clients across automotive, HVAC, security, and B2B SaaS. Filter by industry and service.`,

    keywords: [
      "SEO portfolio",
      "SEO case results",
      `SEO client work ${siteConfig.contact.address.city}`,

      "SEO Semrush results",

      "SEO Search Console proof",
      `SEO agency portfolio ${siteConfig.contact.address.state}`,
    ],
  }), scripts: ldScripts(
    breadcrumbLd([{ name: "Home", path: "/" }, { name: "Portfolio", path: "/portfolio/" }]),
    faqLd(PORTFOLIO_FAQS),
  ) }),
  component: PortfolioIndex,
});

const AGG = [
  { v: "9.6K+", l: "Ranking keywords" },
  { v: "8.0K+", l: "Monthly organic visits" },
  { v: "1,120+", l: "Referring domains" },
  { v: "680K+", l: "GSC impressions / 6 mo" },
];

function PortfolioIndex() {
  const featured = PORTFOLIO.filter((p) => p.featured);
  const spotlight = featured[0];

  return (
    <div>
      <SiteNav />
      <main id="main" tabIndex={-1}>
        <section className="container-page pt-20 md:pt-28 pb-10">
          <EyebrowLabel>Selected ${siteConfig.contact.address.state} work · 2025 — 2026</EyebrowLabel>
          <h1 className="mt-4 font-display text-5xl md:text-7xl leading-[1.02] max-w-4xl text-balance">
            Growth engines,
            <span className="text-gradient"> engineered.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-muted-foreground">
            Every engagement below ships with real Semrush and Search Console
            screenshots — dated, sourced, and named. No stock metrics. No
            anonymous "SaaS clients." This is the standard we hold ourselves to.
          </p>

          <dl className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6 border-t border-border pt-8">
            {AGG.map((s) => (
              <div key={s.l}>
                <dt className="text-[0.7rem] uppercase tracking-widest text-muted-foreground">
                  {s.l}
                </dt>
                <dd className="mt-2 font-display text-3xl md:text-4xl text-gradient">
                  {s.v}
                </dd>
              </div>
            ))}
          </dl>
        </section>

        {spotlight && (
          <section className="container-page py-14">
            <div className="surface-card overflow-hidden grid lg:grid-cols-[1.15fr_1fr]">
              <div className="p-8 md:p-12 flex flex-col justify-center">
                <EyebrowLabel>Featured engagement</EyebrowLabel>
                <h2 className="mt-4 font-display text-3xl md:text-4xl leading-tight">
                  {spotlight.client}
                </h2>
                <p className="mt-4 text-base text-muted-foreground">
                  {spotlight.tagline}
                </p>
                <div className="mt-6 grid grid-cols-3 gap-4">
                  {spotlight.metrics.slice(0, 3).map((m) => (
                    <div key={m.label}>
                      <div className="font-display text-2xl text-primary">
                        {m.value}
                      </div>
                      <div className="mt-1 text-[0.7rem] uppercase tracking-wider text-muted-foreground">
                        {m.label}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-8 flex gap-3">
                  <Link
                    to="/portfolio/$slug/"
                    params={{ slug: spotlight.slug }}
                    className="inline-flex rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground"
                  >
                    View engagement →
                  </Link>
                  {spotlight.hasCaseStudy && (
                    <Link
                      to="/case-studies/$slug/"
                      params={{ slug: spotlight.slug }}
                      className="inline-flex rounded-full border border-border-strong px-5 py-2.5 text-sm hover:border-primary hover:text-primary"
                    >
                      Case study
                    </Link>
                  )}
                </div>
              </div>
              <div className="bg-surface-2 overflow-hidden">
                <Picture
                  variantKey={spotlight.coverVariant}
                  alt={`${spotlight.client} — Semrush results`}
                  sizes="(min-width: 1024px) 50vw, 100vw"
                  priority
                  className="block h-full w-full"
                  imgClassName="h-full w-full object-cover object-top"
                />
              </div>
            </div>
          </section>
        )}

        <section className="container-page py-10">
          <SectionHeading
            eyebrow="All engagements"
            title="Filter by industry, service, or keyword."
            intro="Every project below links to a full breakdown with sourced metrics and — where documented — a long-form case study."
          />
          <div className="mt-10">
            <PortfolioFilters projects={PORTFOLIO} />
          </div>
        </section>

        <section className="container-page py-16">
          <div className="surface-card p-8 md:p-12 grid gap-6 md:grid-cols-[1fr_auto] md:items-center">
            <div>
              <EyebrowLabel>Next quarter</EyebrowLabel>
              <h2 className="mt-3 font-display text-3xl md:text-4xl">
                Want to be the next case study?
              </h2>
              <p className="mt-3 text-muted-foreground max-w-xl">
                We take on a limited number of engagements each quarter so every
                client gets the same level of technical depth. Book a free
                strategy call to see if we're the right fit.
              </p>
            </div>
            <Link
              to="/contact/"
              search={{ location: "", service: "" }}
              className="inline-flex whitespace-nowrap rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground"
            >
              Book a strategy call →
            </Link>
          </div>
        </section>

        <section className="container-page py-20 max-w-3xl">

          <EyebrowLabel>How we build this page</EyebrowLabel>
          <h2 className="mt-4 font-display text-3xl md:text-4xl tracking-tight leading-tight">
            Our method for selecting and documenting work
          </h2>
          <div className="mt-6 space-y-5 text-[1.05rem] leading-relaxed text-foreground/85">
            <p>
              We treat the portfolio as a claims register, not a highlight reel. Every entry has to
              clear a documentation bar before it goes live: a dated baseline captured before our
              first change, a dated snapshot taken at a defined interval afterwards, and a named
              source for each figure — Semrush, Google Search Console, Google Business Profile
              insights, or a client-supplied CRM export. If we cannot attach a source to a number, the
              number does not appear on the page.
            </p>
            <p>
              This is deliberately slower than what most agencies do. It means engagements sit in a
              queue while we gather screenshots and confirm client permission to publish, and it means
              a handful of genuinely strong results never make it onto the site because a client
              preferred to keep the engagement private. We accept that trade because a portfolio full
              of unverifiable claims is worse than a shorter one that survives scrutiny.
            </p>
            <p>
              We also refuse to normalise or annualise figures to make them look larger. If a keyword
              count grew over eight months, we say eight months — not "in under a year" rounded up, and
              not projected forward to a hypothetical twelve-month figure. Readers comparing agencies
              should be able to take the time window at face value.
            </p>
          </div>
        </section>

        <section className="container-page py-16 max-w-3xl border-t border-border">
          <EyebrowLabel>What we publish</EyebrowLabel>
          <h2 className="mt-4 font-display text-3xl md:text-4xl tracking-tight leading-tight">
            The evidence behind each engagement
          </h2>
          <div className="mt-6 space-y-5 text-[1.05rem] leading-relaxed text-foreground/85">
            <p>
              Each engagement page carries at least one first-party screenshot — a Search Console
              performance report, a Google Business Profile insights export, or an analytics traffic
              trend — cropped to show the date range in the tool's own interface rather than a
              redesigned chart. We pair that with a Semrush organic overview where relevant, because
              third-party tools give an outside observer a way to sanity-check our own numbers without
              having to trust our word for it.
            </p>
            <p>
              Where the engagement included technical or content work, we link to the artefacts
              themselves: the schema markup we deployed, the page templates we built, or the citation
              sources we corrected. Seeing the actual output — not just the resulting graph — lets a
              prospective client judge our craft directly, independent of whether the ranking gains
              were driven by SEO, seasonality, or a client's own paid media spend running in parallel.
            </p>
            <p>
              We also disclose confounding factors when we know about them. If a client launched a
              national ad campaign during our engagement window, or a competitor went out of business,
              we say so on the page rather than letting the reader assume our work alone produced every
              percentage point of the movement.
            </p>
          </div>
        </section>

        <section className="container-page py-16 max-w-3xl border-t border-border">
          <EyebrowLabel>For prospective clients</EyebrowLabel>
          <h2 className="mt-4 font-display text-3xl md:text-4xl tracking-tight leading-tight">
            How to evaluate any agency's portfolio critically
          </h2>
          <div className="mt-6 space-y-5 text-[1.05rem] leading-relaxed text-foreground/85">
            <p>
              Whether you're looking at ours or a competitor's, the same checklist applies. Ask
              whether every metric has a stated baseline and end date — a percentage increase without
              a starting number tells you nothing about scale. Ask whether the client is named and
              independently verifiable; an anonymised "leading HVAC company" cannot be checked, which
              is precisely the point of anonymising it. Ask whether the screenshots come from the
              tool's own interface or from a designer's mock-up, since the latter is trivial to
              fabricate.
            </p>
            <p>
              Look for honesty about setbacks. Real SEO campaigns experience algorithm-update dips,
              seasonal troughs, and the occasional page that never ranks despite a solid brief. A
              portfolio where every line only goes up, forever, across every single project, is either
              extraordinarily lucky or has been curated to hide the misses. We'd rather show you a
              project with a flat quarter and explain what we changed than pretend every month compounds
              cleanly.
            </p>
            <p>
              Finally, ask what specifically the agency did — not just what happened to the metric
              afterwards. Correlation between "we started working with this client" and "traffic went
              up" is not proof of causation on its own; a credible agency should be able to point to
              the individual pages, links, or technical fixes that plausibly produced the change, and
              acknowledge the parts of the growth that were probably seasonal, competitive, or driven
              by the client's own marketing running alongside the SEO work.
            </p>
          </div>
        </section>

        <section className="container-page py-16 max-w-3xl border-t border-border">
          <h2 className="font-display text-3xl md:text-4xl tracking-tight leading-tight">
            Frequently asked questions
          </h2>
          <dl className="mt-8 space-y-6">
            {PORTFOLIO_FAQS.map((f) => (
              <div key={f.q} className="rounded-2xl border border-border p-6">
                <dt className="font-display text-lg tracking-tight">{f.q}</dt>
                <dd className="mt-2 text-foreground/85 leading-relaxed">{f.a}</dd>
              </div>
            ))}
          </dl>
        </section>

      </main>
      <SiteFooter />
    </div>
  );
}
